import type { EventCoverSelection } from "@/lib/events/cover-selection"
import { EVENT_COVER_FALLBACK_IMAGE_SRC } from "@/lib/covers/cover-options"
import type { EventData } from "@/lib/events/types"
import {
  CLOUDFLARE_EVENT_COVER_THUMB_VARIANT,
  CLOUDFLARE_EVENT_COVER_VIEWER_VARIANT,
  appendCacheBustToImageUrl,
  buildCloudflareImageUrl,
} from "@/lib/media/cloudflare-urls"

/**
 * Render-time placeholder when an event has no persisted cover.
 * Never send this to POST /api/events or persist in the DB.
 */
export const EVENT_COVER_DISPLAY_PLACEHOLDER = EVENT_COVER_FALLBACK_IMAGE_SRC
/** Event detail hero cover fit policy (separate from lightbox/viewer rules). */
export const EVENT_COVER_HERO_IMAGE_CLASS = "object-cover object-center"
/**
 * Layout hint for `next/image` (CSS px); the runtime multiplies by DPR when requesting
 * the optimizer. Pair with a full viewer `src`, not a thumb variant.
 */
export const EVENT_COVER_HERO_SIZES =
  "(max-width: 47.5rem) 100vw, (max-width: 64rem) 90vw, 47.5rem"

/**
 * Prominent list rows, dashboard cards, marketing tiles — high-fidelity surface.
 * Prefer full `image` (viewer / hero URL); use `imageThumbnailUrl` only when the
 * full URL is missing so we never stretch a CF thumb variant across card slots.
 */
export function eventCoverSrcThumbnail(
  event: Pick<EventData, "image" | "imageThumbnailUrl">,
): string {
  const full = event.image?.trim()
  if (full) return full
  const thumb = event.imageThumbnailUrl?.trim()
  if (thumb) return thumb
  return EVENT_COVER_DISPLAY_PLACEHOLDER
}

/** Hero / full-width cover — viewer (full) variant; thumbnail only as last resort. */
export function eventCoverSrcHero(
  event: Pick<EventData, "image" | "imageThumbnailUrl">,
): string {
  const full = event.image?.trim()
  if (full) return full
  const thumb = event.imageThumbnailUrl?.trim()
  if (thumb) return thumb
  return EVENT_COVER_DISPLAY_PLACEHOLDER
}

/** True when the event has a persisted shell cover (not album display fallback). */
export function eventHasPersistedHeroCover(
  event: Pick<EventData, "image" | "hasPersistedShellCover">,
): boolean {
  if (typeof event.hasPersistedShellCover === "boolean") {
    return event.hasPersistedShellCover
  }
  return Boolean(event.image?.trim())
}

/**
 * Cover picker strip / transient chrome only — not for hero or card thumbnails.
 * Uses the lightweight CF thumb variant when only an id is available.
 */
export function eventCoverPreviewFromCfImageId(imageId: string): string {
  const u = buildCloudflareImageUrl(
    imageId,
    CLOUDFLARE_EVENT_COVER_THUMB_VARIANT,
  )
  return u.trim() || EVENT_COVER_DISPLAY_PLACEHOLDER
}

/**
 * After upload: build the hero URL from the confirmed Cloudflare `imageId` + viewer
 * variant (same source of truth as persistence). Optional `cacheBust` busts CDN/browser
 * cache for immediate freshness.
 */
export function resolveCfCoverDisplayUrlAfterUpload(
  cfImageId: string,
  _viewerUrlFromApi?: string | undefined | null,
  cacheBust?: number,
): string {
  const id = cfImageId.trim()
  if (!id) return ""
  const built = buildCloudflareImageUrl(id, CLOUDFLARE_EVENT_COVER_VIEWER_VARIANT).trim()
  if (!built) return ""
  if (cacheBust === undefined) return built
  return appendCacheBustToImageUrl(built, cacheBust)
}

/** Resolved preview URL for the create-flow cover picker (never persisted). */
export function coverSelectionPreviewSrc(
  value: EventCoverSelection | null,
): string | null {
  if (!value) return null
  if (value.kind === "curated") {
    const u = value.imageUrl?.trim() || null
    if (!u || u.startsWith("blob:") || u.startsWith("data:")) return null
    return u
  }
  const u = value.imageUrl?.trim()
  if (u) return u
  const id = value.cloudflareImageId?.trim()
  if (id) {
    const fromId = buildCloudflareImageUrl(id, CLOUDFLARE_EVENT_COVER_VIEWER_VARIANT).trim()
    if (fromId) return fromId
    return eventCoverPreviewFromCfImageId(id)
  }
  return null
}
